export { newId, isUlid, isUuid, isEntityId } from "./id.js";
export { toStringArray, asStringArray } from "./string-array.js";
export { isUserScope, isDirectoryScope, normalizeSearchScope, } from "./search-scope.js";
// B2B sales pipeline stages
export const CANONICAL_STATES = [
    "Lead", "Qualified", "Discovery", "Proposal", "Demo",
    "Negotiation", "Won", "Lost", "Passed", "Watch",
];
/** Default win probability (%) by pipeline stage when unset on the opportunity. */
export const STAGE_DEFAULT_PROBABILITY = {
    Lead: 10,
    Qualified: 20,
    Discovery: 40,
    Proposal: 50,
    Demo: 60,
    Negotiation: 80,
    Won: 100,
    Lost: 0,
    Passed: 0,
    Watch: 0,
};
export function stageDefaultProbability(stage) {
    if (!stage)
        return STAGE_DEFAULT_PROBABILITY.Lead;
    return STAGE_DEFAULT_PROBABILITY[stage] ?? STAGE_DEFAULT_PROBABILITY.Lead;
}
/** Closed / parked stages excluded from weighted open pipeline. */
export const TERMINAL_FORECAST_STAGES = new Set([
    "Won",
    "Lost",
    "Passed",
    "Watch",
]);
export function buildDefaultDealStages() {
    return CANONICAL_STATES.map((name, order) => {
        const type = name === "Won" ? "won" : name === "Lost" || name === "Passed" ? "lost" : "open";
        const forecastCategory = name === "Won"
            ? "closed"
            : name === "Lost" || name === "Passed" || name === "Watch"
                ? "omit"
                : name === "Negotiation"
                    ? "commit"
                    : "pipeline";
        return {
            id: name.toLowerCase().replace(/\s+/g, "_"),
            name,
            order,
            type,
            probability: STAGE_DEFAULT_PROBABILITY[name],
            forecastCategory,
        };
    });
}
// Prompt kinds
export const PROMPT_KINDS = [
    "qualify", "qualify.es", "research", "pdf", "fix-portal", "assistant",
    "proposal-prefill", "narrative-ingest", "ai-prospect",
    "proposal-form-interpret", "proposal-drive",
    "scan", "proposal-draft", "demo-prep",
    "submit", "demo-plan", "demo-practice",
    "demo-debrief", "outreach", "outreach.linkedin", "deep", "patterns",
    "followup", "batch", "training", "project", "latex",
    "email", "_shared", "_profile",
    "opportunity-parse", "company-research",
    "opportunity-research", "opportunity-research-light", "opportunity-research-deep",
    "negotiation-review", "discovery-prep",
];
/** Legacy kind strings remapped before template lookup. */
export const PROMPT_KIND_ALIASES = {
    evaluate: "qualify",
    "apply-form-interpret": "proposal-form-interpret",
    "cv-ingest": "narrative-ingest",
    "ai-hunt": "ai-prospect",
    "apply-prefill": "proposal-prefill",
    "apply-drive": "proposal-drive",
};
export function resolvePromptKind(kind) {
    return PROMPT_KIND_ALIASES[kind] ?? kind;
}
/** Resolve signalsUrl from a company/user-company record. */
export function resolveSignalsUrl(record) {
    if (!record)
        return undefined;
    const v = record.signalsUrl;
    return typeof v === "string" && v.trim() ? v : undefined;
}
const LINKEDIN_TYPES = [
    "linkedin_step_connect",
    "linkedin_step_message",
    "linkedin_step_view_profile",
    "linkedin_step_interact_post",
];
export function isLinkedInTaskType(type) {
    return LINKEDIN_TYPES.includes(type);
}
export function normalizeTaskType(raw, channel) {
    const t = (raw ?? "").toLowerCase().trim();
    if (t === "call" || t === "phone")
        return "call";
    if (t === "manual_email" || t === "email" || t === "auto_email")
        return "manual_email";
    if (t === "linkedin_step_connect" || t === "linkedin_connect")
        return "linkedin_step_connect";
    if (t === "linkedin_step_message" || t === "linkedin_message")
        return "linkedin_step_message";
    if (t === "linkedin_step_view_profile" || t === "linkedin_view_profile")
        return "linkedin_step_view_profile";
    if (t === "linkedin_step_interact_post" || t === "linkedin_interact_post")
        return "linkedin_step_interact_post";
    if (t.startsWith("linkedin"))
        return "linkedin_step_connect";
    if (t === "action_item" || t === "action" || t === "other")
        return "action_item";
    const ch = (channel ?? "").toLowerCase();
    if (ch === "call" || ch === "phone")
        return "call";
    if (ch === "email")
        return "manual_email";
    if (ch.includes("linkedin"))
        return "linkedin_step_connect";
    return "action_item";
}
export function normalizeTaskPriority(raw) {
    const p = (raw ?? "").toLowerCase();
    if (p === "high" || p === "1")
        return "high";
    if (p === "low" || p === "3")
        return "low";
    return "medium";
}
export function normalizeTaskStatus(f) {
    if (f.isArchived || f.status === "archived")
        return "archived";
    if (f.status === "skipped")
        return "skipped";
    if (f.completed === true ||
        f.status === "completed" ||
        f.status === "done" ||
        f.cadenceStatus === "done") {
        return "completed";
    }
    return "pending";
}
export function taskTypeToHubTab(type) {
    if (type === "call")
        return "calls";
    if (type === "manual_email")
        return "emails";
    if (isLinkedInTaskType(type))
        return "linkedin";
    return "action_items";
}
export function followUpToTask(f) {
    const type = normalizeTaskType(f.type, f.channel);
    const status = normalizeTaskStatus(f);
    const dueAt = f.dueDate || f.nextFollowUp || f.createdAt;
    const sequenceId = f.sequenceId ||
        f.tags?.find((t) => t !== "sequence" && t.length > 8) ||
        (f.automatedTrigger?.startsWith("sequence:")
            ? f.automatedTrigger.split(":")[1]
            : undefined);
    const title = f.title?.trim() ||
        (type === "call"
            ? `Call: ${f.leadName || f.dealName || "contact"}`
            : type === "manual_email"
                ? `Email: ${f.leadName || f.dealName || "contact"}`
                : isLinkedInTaskType(type)
                    ? `LinkedIn: ${f.leadName || f.dealName || "contact"}`
                    : `Follow up · ${f.dealName || f.company || "task"}`);
    return {
        taskId: String(f.followupId ?? f.SK ?? ""),
        userId: f.userId,
        type,
        title,
        description: f.description,
        notes: f.notes,
        customMessage: f.customMessage,
        priority: normalizeTaskPriority(f.priority),
        status,
        dueAt,
        leadId: f.leadId,
        leadName: f.leadName,
        company: f.company || f.accountId || "",
        accountId: f.accountId,
        dealName: f.dealName || "",
        opportunityId: f.opportunityId,
        sequenceId,
        enrollmentId: f.enrollmentId ?? f.leadId,
        sequenceStepId: f.sequenceStepId,
        linkedinUrl: f.linkedinUrl,
        assigneeId: f.assigneeId ?? f.createdBy ?? f.userId,
        tags: f.tags,
        automatedTrigger: f.automatedTrigger,
        completedAt: f.completedAt,
        createdAt: f.createdAt,
        updatedAt: f.updatedAt,
    };
}
export function emptyTaskFacets() {
    return {
        all: 0,
        calls: 0,
        emails: 0,
        linkedin: 0,
        action_items: 0,
        recommended: 0,
        pending: 0,
        completed: 0,
        overdue: 0,
    };
}
export function resolveHubSpotObjectId(crm, legacyId) {
    return crm?.hubspot?.objectId ?? legacyId ?? undefined;
}
export function resolveApolloPersonId(crm, legacyId) {
    return crm?.apollo?.personId ?? legacyId ?? undefined;
}
export function resolveApolloAccountId(crm, legacyId) {
    return crm?.apollo?.accountId ?? legacyId ?? undefined;
}
export function resolveSalesforceObjectId(crm, legacyId) {
    return crm?.salesforce?.objectId ?? legacyId ?? undefined;
}
export function mergeEntityCrm(existing, patch) {
    if (!existing && !patch)
        return undefined;
    return {
        ...existing,
        ...patch,
        hubspot: { ...existing?.hubspot, ...patch?.hubspot },
        apollo: { ...existing?.apollo, ...patch?.apollo },
        salesforce: { ...existing?.salesforce, ...patch?.salesforce },
        origin: patch?.origin ?? existing?.origin,
        hubspotSyncedAt: patch?.hubspotSyncedAt ?? existing?.hubspotSyncedAt,
        apolloSyncedAt: patch?.apolloSyncedAt ?? existing?.apolloSyncedAt,
        salesforceSyncedAt: patch?.salesforceSyncedAt ?? existing?.salesforceSyncedAt,
        lastRemoteUpdatedAt: patch?.lastRemoteUpdatedAt ?? existing?.lastRemoteUpdatedAt,
    };
}
/** Built-in market-pulse / intent signal providers (catalog type: signal) */
export const MARKET_PULSE_PROVIDERS = [
    { id: "newsapi", name: "NewsAPI", type: "news" },
    { id: "sec-edgar", name: "SEC EDGAR", type: "regulatory" },
    { id: "apollo-funding", name: "Apollo funding", type: "funding" },
    { id: "apollo-tech", name: "Apollo tech stack", type: "tech" },
    {
        id: "apollo-intent",
        name: "Apollo intent proxy",
        type: "intent",
    },
    {
        id: "intent-webhook",
        name: "Intent webhook ingest",
        type: "intent",
    },
];
/** Board aggregators with a live hiring scanner */
export const IMPLEMENTED_BOARD_PROVIDER_IDS = [
    "remoteok",
    "remotive",
    "workingnomads",
    "hackernews",
    "arbeitnow",
];
/** ATS vendors with a live careers scanner */
export const IMPLEMENTED_ATS_PROVIDER_IDS = [
    "greenhouse",
    "ashby",
    "lever",
];
/** Signal providers with a live pull or ingest adapter */
export const IMPLEMENTED_SIGNAL_PROVIDER_IDS = [
    "newsapi",
    "sec-edgar",
    "apollo-funding",
    "apollo-tech",
    "apollo-intent",
    "intent-webhook",
];
export const DEFAULT_ENRICHMENT_WATERFALL = {
    waterfallEmail: true,
    waterfallPhone: true,
    providerOrder: ["apollo"],
};
export const DEFAULT_ENRICHMENT_SCHEDULE = {
    enabled: false,
    kind: "missing_email",
    cadenceDays: 7,
};
export const DEFAULT_ENRICHMENT_REFRESH_DAYS = 90;
export const ANALYTICS_CATALOG_DASHBOARDS = [
    {
        id: "outbound",
        name: "Outbound dashboard",
        description: "Emails, calls, sequence enrollments, and tasks at a glance.",
        category: "Outbound",
        widgets: [
            { id: "kpi-email", title: "Emails sent", kind: "kpi", metrics: ["emails_sent"] },
            { id: "kpi-open", title: "Emails opened", kind: "kpi", metrics: ["emails_opened"] },
            { id: "kpi-reply", title: "Emails replied", kind: "kpi", metrics: ["emails_replied"] },
            { id: "kpi-calls", title: "Calls logged", kind: "kpi", metrics: ["calls_logged"] },
            { id: "kpi-conn", title: "Calls connected", kind: "kpi", metrics: ["calls_connected"] },
            { id: "kpi-seq", title: "Sequence enrollments", kind: "kpi", metrics: ["sequence_enrollments"] },
            { id: "bar-week", title: "Activity by week", kind: "bar", metrics: ["emails_sent", "calls_logged"], groupBy: "week" },
        ],
    },
    {
        id: "deal-analytics",
        name: "Deal analytics",
        description: "Pipeline funnel, win rate, forecast, and deal volume.",
        category: "Deals",
        widgets: [
            { id: "deal-summary", title: "Deal summary", kind: "deal_summary" },
            { id: "kpi-weighted", title: "Weighted pipeline", kind: "kpi", metrics: ["pipeline_weighted"] },
            { id: "kpi-won", title: "Deals won", kind: "kpi", metrics: ["deals_won"] },
            { id: "bar-stage", title: "Deal volume by stage", kind: "bar", metrics: ["deals_created"], groupBy: "deal_stage" },
        ],
    },
    {
        id: "email-engagement",
        name: "Email engagement",
        description: "Email funnel and weekly send activity.",
        category: "Engage",
        widgets: [
            { id: "funnel-email", title: "Email funnel", kind: "funnel", metrics: ["emails_sent", "emails_opened", "emails_clicked", "emails_replied"] },
            { id: "bar-email-week", title: "Emails by week", kind: "bar", metrics: ["emails_sent", "emails_replied"], groupBy: "week" },
        ],
    },
    {
        id: "call-engagement",
        name: "Call engagement",
        description: "Call volume, connect rate, and dispositions.",
        category: "Engage",
        widgets: [
            { id: "kpi-calls2", title: "Calls logged", kind: "kpi", metrics: ["calls_logged"] },
            { id: "kpi-conn2", title: "Calls connected", kind: "kpi", metrics: ["calls_connected"] },
            { id: "bar-disp", title: "By disposition", kind: "bar", metrics: ["calls_logged"], groupBy: "disposition" },
            { id: "bar-call-week", title: "Calls by week", kind: "bar", metrics: ["calls_logged", "calls_connected"], groupBy: "week" },
        ],
    },
    {
        id: "sequence-performance",
        name: "Sequence performance",
        description: "Enrollment and reply performance across sequences.",
        category: "Engage",
        widgets: [
            { id: "table-seq", title: "Sequences", kind: "table", metrics: ["sequence_enrollments", "emails_replied"], groupBy: "sequence" },
            { id: "kpi-enroll", title: "Enrollments", kind: "kpi", metrics: ["sequence_enrollments"] },
        ],
    },
    {
        id: "prospecting-activity",
        name: "Prospecting activity",
        description: "Combined outreach volume across channels.",
        category: "Outbound",
        widgets: [
            { id: "kpi-p1", title: "Emails", kind: "kpi", metrics: ["emails_sent"] },
            { id: "kpi-p2", title: "Calls", kind: "kpi", metrics: ["calls_logged"] },
            { id: "kpi-p3", title: "Enrollments", kind: "kpi", metrics: ["sequence_enrollments"] },
            { id: "kpi-p4", title: "Tasks done", kind: "kpi", metrics: ["tasks_completed"] },
            { id: "bar-p", title: "Weekly prospecting", kind: "bar", metrics: ["emails_sent", "calls_logged", "tasks_completed"], groupBy: "week" },
        ],
    },
    {
        id: "meeting-insights",
        name: "Meeting insights",
        description: "Booked, completed, and no-show meetings.",
        category: "Engage",
        widgets: [
            { id: "kpi-m1", title: "Booked", kind: "kpi", metrics: ["meetings_booked"] },
            { id: "kpi-m2", title: "Completed", kind: "kpi", metrics: ["meetings_completed"] },
            { id: "kpi-m3", title: "No-shows", kind: "kpi", metrics: ["meetings_no_show"] },
            { id: "bar-m", title: "Meetings by week", kind: "bar", metrics: ["meetings_booked", "meetings_completed"], groupBy: "week" },
        ],
    },
    {
        id: "deliverability",
        name: "Deliverability rates & scores",
        description: "Mailbox health proxies from email delivery outcomes.",
        category: "Admin",
        widgets: [
            { id: "kpi-d1", title: "Sent", kind: "kpi", metrics: ["emails_sent"] },
            { id: "kpi-d2", title: "Bounced", kind: "kpi", metrics: ["emails_bounced"] },
            { id: "funnel-d", title: "Delivery funnel", kind: "funnel", metrics: ["emails_sent", "emails_opened", "emails_replied", "emails_bounced"] },
        ],
    },
];
export const ANALYTICS_CATALOG_REPORTS = [
    {
        id: "email-funnel",
        name: "Prospecting email performance funnel",
        description: "Sent → opened → clicked → replied.",
        category: "Emails",
        metricKeys: ["emails_sent", "emails_opened", "emails_clicked", "emails_replied"],
        visualization: "funnel",
    },
    {
        id: "call-funnel",
        name: "Prospecting call performance funnel",
        description: "Logged → connected calls.",
        category: "Calls",
        metricKeys: ["calls_logged", "calls_connected"],
        visualization: "funnel",
    },
    {
        id: "email-by-week",
        name: "Email activity by week",
        description: "Weekly email volume and replies.",
        category: "Emails",
        metricKeys: ["emails_sent", "emails_replied"],
        groupBy: "week",
        visualization: "bar",
    },
    {
        id: "call-by-week",
        name: "Call activity by week",
        description: "Weekly call volume and connects.",
        category: "Calls",
        metricKeys: ["calls_logged", "calls_connected"],
        groupBy: "week",
        visualization: "bar",
    },
    {
        id: "high-performing-sequences",
        name: "High performing sequences",
        description: "Sequences ranked by enrollments and replies.",
        category: "Sequences",
        metricKeys: ["sequence_enrollments", "emails_replied"],
        groupBy: "sequence",
        visualization: "table",
    },
    {
        id: "low-performing-sequences",
        name: "Low performing sequences",
        description: "Sequences with enrollments but weak replies.",
        category: "Sequences",
        metricKeys: ["sequence_enrollments", "emails_replied"],
        groupBy: "sequence",
        visualization: "table",
    },
    {
        id: "deal-volume-by-stage",
        name: "Deal volume by stage",
        description: "Open deal counts grouped by stage.",
        category: "Deals",
        metricKeys: ["deals_created"],
        groupBy: "deal_stage",
        visualization: "bar",
    },
    {
        id: "deals-won-by-month",
        name: "Deals won by month",
        description: "Closed-won deals over time.",
        category: "Deals",
        metricKeys: ["deals_won"],
        groupBy: "week",
        visualization: "line",
    },
    {
        id: "team-activity-week",
        name: "Team activity this week",
        description: "Emails and calls in the selected range.",
        category: "Team",
        metricKeys: ["emails_sent", "calls_logged"],
        visualization: "kpi",
    },
    {
        id: "tasks-by-week",
        name: "Tasks completed by week",
        description: "Completed task volume over time.",
        category: "Tasks",
        metricKeys: ["tasks_completed"],
        groupBy: "week",
        visualization: "bar",
    },
];
export function salesProviderCategory(type) {
    if (type === "ats" || type === "board" || type === "local-parser") {
        return "hiring";
    }
    if (type === "signal")
        return "market_signal";
    return "integration";
}
export const DEFAULT_SEQUENCE_SCHEDULE = {
    timezone: "UTC",
    skipHolidays: true,
    timeBlocks: [
        { dayOfWeek: 1, startTime: "09:00", endTime: "17:00" },
        { dayOfWeek: 2, startTime: "09:00", endTime: "17:00" },
        { dayOfWeek: 3, startTime: "09:00", endTime: "17:00" },
        { dayOfWeek: 4, startTime: "09:00", endTime: "17:00" },
        { dayOfWeek: 5, startTime: "09:00", endTime: "17:00" },
    ],
};
export const DEFAULT_SEQUENCE_RULES = {
    finishOnReply: true,
    pauseOnOoo: true,
    finishOnMeetingBooked: true,
};
export const DEFAULT_CALL_PURPOSES = [
    { id: "prospecting", name: "Prospecting call", order: 0 },
    { id: "opportunity", name: "Opportunity call", order: 1 },
    { id: "support", name: "Support call", order: 2 },
    { id: "other", name: "Other call", order: 3 },
];
export const DEFAULT_CALL_DISPOSITIONS = [
    { id: "no_answer", name: "No answer", result: "not_connected", order: 0 },
    {
        id: "left_voicemail",
        name: "Left voicemail",
        result: "not_connected",
        order: 1,
    },
    { id: "busy", name: "Busy", result: "not_connected", order: 2 },
    { id: "gatekeeper", name: "Gatekeeper", result: "not_connected", order: 3 },
    {
        id: "bad_number",
        name: "Bad/wrong number",
        result: "not_connected",
        order: 4,
    },
    {
        id: "not_in_service",
        name: "Not in service",
        result: "not_connected",
        order: 5,
    },
    {
        id: "connected_positive",
        name: "Connected - Positive",
        result: "connected",
        sentiment: "positive",
        order: 6,
    },
    {
        id: "connected_neutral",
        name: "Connected - Neutral",
        result: "connected",
        sentiment: "neutral",
        order: 7,
    },
    {
        id: "connected_negative",
        name: "Connected - Negative",
        result: "connected",
        sentiment: "negative",
        order: 8,
    },
];
/** Seed recording rules mirroring Apollo defaults (compliance scaffolding, not legal advice). */
export const DEFAULT_RECORDING_RULES = [
    {
        id: "us-two-party",
        name: "US two-party consent states",
        country: "US",
        states: [
            "CA",
            "CO",
            "CT",
            "DE",
            "FL",
            "IL",
            "MD",
            "MA",
            "MI",
            "MT",
            "NV",
            "NH",
            "OR",
            "PA",
            "VT",
            "WA",
        ],
        inboundParties: "none",
        outboundParties: "none",
        playAnnouncementInbound: true,
        playAnnouncementOutbound: true,
        announcementText: "This call is being recorded and transcribed for quality and analytics purposes.",
        createdAt: "1970-01-01T00:00:00.000Z",
        updatedAt: "1970-01-01T00:00:00.000Z",
    },
    {
        id: "ca",
        name: "Canada",
        country: "CA",
        inboundParties: "none",
        outboundParties: "none",
        playAnnouncementInbound: true,
        playAnnouncementOutbound: true,
        createdAt: "1970-01-01T00:00:00.000Z",
        updatedAt: "1970-01-01T00:00:00.000Z",
    },
    {
        id: "gb",
        name: "United Kingdom",
        country: "GB",
        inboundParties: "none",
        outboundParties: "none",
        playAnnouncementInbound: true,
        playAnnouncementOutbound: true,
        createdAt: "1970-01-01T00:00:00.000Z",
        updatedAt: "1970-01-01T00:00:00.000Z",
    },
    {
        id: "eu",
        name: "European Union",
        country: "EU",
        inboundParties: "none",
        outboundParties: "none",
        playAnnouncementInbound: true,
        playAnnouncementOutbound: true,
        createdAt: "1970-01-01T00:00:00.000Z",
        updatedAt: "1970-01-01T00:00:00.000Z",
    },
    {
        id: "au",
        name: "Australia",
        country: "AU",
        inboundParties: "none",
        outboundParties: "none",
        playAnnouncementInbound: true,
        playAnnouncementOutbound: true,
        createdAt: "1970-01-01T00:00:00.000Z",
        updatedAt: "1970-01-01T00:00:00.000Z",
    },
];
export const DEFAULT_DIALER_PREFERENCES = {
    outboundMode: "voip",
    showConnectionErrors: false,
    recordPref: true,
};
export const DEFAULT_SCORECARD_CRITERIA = [
    { id: "discovery", label: "Discovery quality", maxScore: 5, order: 0 },
    { id: "value_prop", label: "Value proposition clarity", maxScore: 5, order: 1 },
    { id: "objection_handling", label: "Objection handling", maxScore: 5, order: 2 },
    { id: "next_steps", label: "Clear next steps", maxScore: 5, order: 3 },
];
export const EMPTY_SEQUENCE_STATS = {
    uniqueScheduled: 0,
    uniqueDelivered: 0,
    uniqueOpened: 0,
    uniqueClicked: 0,
    uniqueReplied: 0,
    uniqueBounced: 0,
    activeEnrollments: 0,
    finishedEnrollments: 0,
    numSteps: 0,
};
export const DEFAULT_WEEKLY_HOURS = [
    { dayOfWeek: 1, startTime: "09:00", endTime: "17:00" },
    { dayOfWeek: 2, startTime: "09:00", endTime: "17:00" },
    { dayOfWeek: 3, startTime: "09:00", endTime: "17:00" },
    { dayOfWeek: 4, startTime: "09:00", endTime: "17:00" },
    { dayOfWeek: 5, startTime: "09:00", endTime: "17:00" },
];
export function slugifyBooking(input) {
    return input
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 48) || "meet";
}
export const DEFAULT_WORKFLOW_SETTINGS = {
    reEnrollmentDays: 180,
    visibility: "private",
};
export const EMPTY_WORKFLOW_STATS = {
    enrolled: 0,
    completed: 0,
    failed: 0,
    running: 0,
};
export const WORKFLOW_TEMPLATES = [
    {
        id: "lead-prioritization",
        name: "Lead prioritization",
        description: "Weekly schedule: filter high-intent people and enroll them into a sequence.",
        category: "Outbound",
        draft: {
            name: "Lead prioritization",
            targetType: "people",
            trigger: {
                type: "schedule",
                scheduleInterval: "weekly",
                scheduleDayOfWeek: 1,
                scheduleTime: "09:00",
            },
            filterRules: [{ field: "title", op: "contains", value: "VP" }],
            nodes: [
                {
                    id: "n1",
                    type: "add_to_sequence",
                    label: "Add to sequence",
                    config: { sequenceId: "" },
                    next: "n2",
                },
                {
                    id: "n2",
                    type: "create_task",
                    label: "Create follow-up task",
                    config: {
                        title: "Prioritize high-intent lead",
                        type: "action_item",
                        priority: "high",
                    },
                },
            ],
            entryNodeId: "n1",
        },
    },
    {
        id: "new-lead-sequence",
        name: "New contact → sequence",
        description: "When a contact is created, enroll in a sequence and create a task.",
        category: "Inbound",
        draft: {
            name: "New contact → sequence",
            targetType: "people",
            trigger: { type: "lead_created" },
            filterRules: [],
            nodes: [
                {
                    id: "n1",
                    type: "add_to_sequence",
                    label: "Add to sequence",
                    config: { sequenceId: "" },
                    next: "n2",
                },
                {
                    id: "n2",
                    type: "create_task",
                    label: "Create intro task",
                    config: {
                        title: "Introduce new contact",
                        type: "call",
                        priority: "medium",
                    },
                },
            ],
            entryNodeId: "n1",
        },
    },
    {
        id: "list-join-nurture",
        name: "List join nurture",
        description: "When someone joins a list, wait then enroll in a sequence.",
        category: "Nurture",
        draft: {
            name: "List join nurture",
            targetType: "people",
            trigger: { type: "list_joined", listId: "" },
            filterRules: [],
            nodes: [
                {
                    id: "n1",
                    type: "wait",
                    label: "Wait 1 day",
                    config: { waitDays: 1 },
                    next: "n2",
                },
                {
                    id: "n2",
                    type: "add_to_sequence",
                    label: "Add to sequence",
                    config: { sequenceId: "" },
                },
            ],
            entryNodeId: "n1",
        },
    },
    {
        id: "reply-cleanup",
        name: "Reply finished cleanup",
        description: "When a contact replies in a sequence, create an owner task.",
        category: "Engage",
        draft: {
            name: "Reply finished cleanup",
            targetType: "people",
            trigger: { type: "sequence_replied" },
            filterRules: [],
            nodes: [
                {
                    id: "n1",
                    type: "create_task",
                    label: "Handle reply",
                    config: {
                        title: "Respond to sequence reply",
                        type: "manual_email",
                        priority: "high",
                    },
                },
            ],
            entryNodeId: "n1",
        },
    },
    {
        id: "renewal-reminder",
        name: "Renewal reminder (lite)",
        description: "When a contact field updates, add to a renewal list and sequence.",
        category: "Retention",
        draft: {
            name: "Renewal reminder",
            targetType: "people",
            trigger: { type: "lead_updated", fieldKey: "renewalDate" },
            filterRules: [],
            nodes: [
                {
                    id: "n1",
                    type: "add_to_list",
                    label: "Add to renewals list",
                    config: { listId: "" },
                    next: "n2",
                },
                {
                    id: "n2",
                    type: "add_to_sequence",
                    label: "Add to renewal sequence",
                    config: { sequenceId: "" },
                },
            ],
            entryNodeId: "n1",
        },
    },
    {
        id: "website-high-intent-task",
        name: "High-intent website visit → task",
        description: "When an identified contact hits a high-intent page, create a follow-up task.",
        category: "Inbound",
        draft: {
            name: "High-intent website visit → task",
            targetType: "people",
            trigger: { type: "website_visited", minIntent: "high" },
            filterRules: [],
            nodes: [
                {
                    id: "n1",
                    type: "create_task",
                    label: "Call website visitor",
                    config: {
                        title: "Follow up — website visit",
                        type: "call",
                        priority: "high",
                    },
                },
            ],
            entryNodeId: "n1",
        },
    },
    {
        id: "website-visit-list",
        name: "Website visit → list",
        description: "Add identified website visitors to a list for outbound.",
        category: "Inbound",
        draft: {
            name: "Website visit → list",
            targetType: "people",
            trigger: { type: "website_visited" },
            filterRules: [],
            nodes: [
                {
                    id: "n1",
                    type: "add_to_list",
                    label: "Add to website visitors list",
                    config: { listId: "" },
                    next: "n2",
                },
                {
                    id: "n2",
                    type: "create_task",
                    label: "Review visitor",
                    config: {
                        title: "Review website visitor",
                        type: "action_item",
                        priority: "medium",
                    },
                },
            ],
            entryNodeId: "n1",
        },
    },
    {
        id: "website-visit-sequence",
        name: "Website visit → sequence",
        description: "Enroll identified visitors into a sequence after they browse.",
        category: "Inbound",
        draft: {
            name: "Website visit → sequence",
            targetType: "people",
            trigger: { type: "website_visited", minIntent: "medium" },
            filterRules: [],
            nodes: [
                {
                    id: "n1",
                    type: "add_to_sequence",
                    label: "Add to sequence",
                    config: { sequenceId: "" },
                    next: "n2",
                },
                {
                    id: "n2",
                    type: "notify_email",
                    label: "Notify owner",
                    config: { subject: "Website visitor enrolled", body: "" },
                },
            ],
            entryNodeId: "n1",
        },
    },
    {
        id: "form-submitted-sequence",
        name: "Form submitted → sequence",
        description: "Enroll new form leads into a sequence immediately.",
        category: "Inbound",
        draft: {
            name: "Form submitted → sequence",
            targetType: "people",
            trigger: { type: "form_submitted" },
            filterRules: [],
            nodes: [
                {
                    id: "n1",
                    type: "add_to_sequence",
                    label: "Add to sequence",
                    config: { sequenceId: "" },
                    next: "n2",
                },
                {
                    id: "n2",
                    type: "create_task",
                    label: "Follow up on form lead",
                    config: {
                        title: "Follow up — form submission",
                        type: "call",
                        priority: "high",
                    },
                },
            ],
            entryNodeId: "n1",
        },
    },
    {
        id: "form-submitted-list",
        name: "Form submitted → list",
        description: "Add form submitters to a list and create a task.",
        category: "Inbound",
        draft: {
            name: "Form submitted → list",
            targetType: "people",
            trigger: { type: "form_submitted" },
            filterRules: [],
            nodes: [
                {
                    id: "n1",
                    type: "add_to_list",
                    label: "Add to inbound list",
                    config: { listId: "" },
                    next: "n2",
                },
                {
                    id: "n2",
                    type: "create_task",
                    label: "Review form lead",
                    config: {
                        title: "Review form lead",
                        type: "action_item",
                        priority: "medium",
                    },
                },
            ],
            entryNodeId: "n1",
        },
    },
    {
        id: "form-submitted-notify",
        name: "Form submitted → notify",
        description: "Notify the owner when a form is submitted.",
        category: "Inbound",
        draft: {
            name: "Form submitted → notify",
            targetType: "people",
            trigger: { type: "form_submitted" },
            filterRules: [],
            nodes: [
                {
                    id: "n1",
                    type: "notify_email",
                    label: "Email owner",
                    config: {
                        subject: "New form submission",
                        body: "A lead submitted your form.",
                    },
                },
            ],
            entryNodeId: "n1",
        },
    },
];
// ---- Sales Move (Home command queue) ----
export * from "./sales-move.js";
// ---- Canonical directory + import/export (DynamoDB-aligned) ----
export * from "./canonical/index.js";
//# sourceMappingURL=index.js.map